import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sparkle } from '@phosphor-icons/react';
import { applyPageSeo } from '../utils/seo';

const SERVICE_CHIPS = [
  { label: 'Website', to: '/services/website-development' },
  { label: 'Software', to: '/services/custom-software-erp' },
  { label: 'AI Automation', to: '/services/ai-automation' },
  { label: 'WhatsApp API', to: '/services/whatsapp-business-automation' },
  { label: 'IVR', to: '/services/ivr-ai-voice-calling' },
  { label: 'Digital Marketing', to: '/services/digital-marketing-seo' }
];

const HERO_CODE = `const future = await buildNextBigThing();
if (innovative) {
  scaleToMillions();
  disruptMarket();
}`;

const highlightCode = (source) => {
  const escaped = source
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\b(const|await|if)\b/g, '<span class="hero-kw">$1</span>')
    .replace(/\b(future|innovative)\b/g, '<span class="hero-var">$1</span>')
    .replace(/\b(buildNextBigThing|scaleToMillions|disruptMarket)\b/g, '<span class="hero-fn">$1</span>');
};

const HeroCodeTyping = () => {
  const [shown, setShown] = useState('');

  useEffect(() => {
    let i = 0;
    let timer;
    const type = () => {
      i += 1;
      setShown(HERO_CODE.slice(0, i));
      if (i < HERO_CODE.length) {
        timer = window.setTimeout(type, HERO_CODE[i - 1] === '\n' ? 220 : 42);
      } else {
        timer = window.setTimeout(() => {
          i = 0;
          setShown('');
          timer = window.setTimeout(type, 480);
        }, 2600);
      }
    };
    timer = window.setTimeout(type, 400);
    return () => window.clearTimeout(timer);
  }, []);

  const lines = shown.split('\n');

  return (
    <div className="space-y-2.5 font-mono text-sm leading-relaxed min-h-[168px]">
      {lines.map((line, index) => (
        <div key={`${index}-${line.length}`} className="flex items-start gap-3">
          <span className="text-brand-600 dark:text-brand-400 font-semibold w-4 shrink-0">{index + 1}</span>
          <span
            className="text-gray-700 dark:text-gray-300 whitespace-pre"
          >
            <span dangerouslySetInnerHTML={{ __html: highlightCode(line) }} />
            {index === lines.length - 1 && <span className="hero-caret" />}
          </span>
        </div>
      ))}
    </div>
  );
};

const Hero = () => {
  useEffect(() => {
    return applyPageSeo({
    title: 'Software & IT Solutions Company in Satara | Growmore IT Services',
    description:
      'Growmore IT Services delivers websites, mobile apps, ERP software, WhatsApp automation, IVR, Bulk SMS, AI voice calling, cyber security, and digital marketing across India.',
    keywords:
      'IT company in Satara, Software & IT Solutions Company in Satara, Website Development Company in Satara, Mobile App Development Company in Satara, Software Development Company in Satara, AI Automation Company in Satara, WhatsApp Business Automation in Satara, IVR Service Provider in Satara, Bulk SMS Service Provider in Satara, Digital Marketing Company in Satara, Cyber Security Company in Satara, Paid Internship in Satara, Growmore IT Services',
    path: '/'
    });
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Growmore IT Services',
    description:
      'Software and IT solutions company in Satara delivering websites, mobile apps, ERP software, WhatsApp automation, IVR, Bulk SMS, AI voice calling, cyber security and digital marketing across India.',
    areaServed: ['Satara', 'Maharashtra', 'India'],
    url: 'https://growmoreitservices.com/'
  };

  return (
    <section className="relative overflow-hidden pt-24 pb-8 sm:pt-20 sm:pb-12 lg:min-h-screen lg:flex lg:items-center lg:justify-center lg:pb-0">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Enhanced Background Blobs */}
      <div className="absolute inset-0 overflow-hidden -z-10 bg-gradient-to-br from-white via-gray-50 to-blue-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 transition-colors">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-brand-400/20 to-accent-purple-300/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-70 animate-blob" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-br from-accent-cyan-300/20 to-brand-500/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-32 left-1/2 w-96 h-96 bg-gradient-to-br from-accent-pink-300/20 to-brand-600/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-70 animate-blob animation-delay-4000" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5 dark:opacity-10" />
      </div>

      <div className="section-max-width px-4 md:px-6 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left z-10 w-full">
          <div className="inline-flex items-center gap-2 max-w-full px-3 py-1.5 sm:px-4 sm:py-2 rounded-full badge-primary mb-3 sm:mb-5">
            <Sparkle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-600 dark:text-brand-400 shrink-0" weight="fill" />
            <span className="font-semibold tracking-wide uppercase text-[10px] sm:text-xs leading-tight">Software & IT Solutions Company in Satara</span>
          </div>

          <h1 className="text-display text-[1.85rem] sm:text-5xl md:text-[3.25rem] lg:text-[3.45rem] xl:text-6xl hero-title-base leading-[1.18] sm:leading-[1.12] tracking-tight w-full animate-fade-in">
            <span className="block">Build Smarter.</span>
            <span className="block text-gradient sm:whitespace-nowrap">Grow Faster.</span>
            <span className="sr-only"> - Software &amp; IT Solutions Company in Satara | Growmore IT Services</span>
          </h1>

          <nav aria-label="Core services" className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-2 w-full">
            {SERVICE_CHIPS.map((chip, index) => (
              <Link
                key={chip.to}
                to={chip.to}
                style={{ animationDelay: `${index * 70}ms` }}
                className="animate-fade-in px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-brand-800 dark:text-brand-200 bg-white/80 dark:bg-dark-800/80 border border-brand-200/70 dark:border-brand-500/30 hover:border-brand-500 hover:shadow-medium hover:-translate-y-0.5 smooth-transition"
              >
                {chip.label}
              </Link>
            ))}
          </nav>

          <p className="text-body mt-4 sm:mt-6 mb-6 sm:mb-10 text-sm sm:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed animate-fade-in">
            Growmore IT Services delivers AI-powered websites, mobile apps, ERP software, WhatsApp automation, IVR, Bulk SMS, AI voice calling, cyber security, and digital marketing to help businesses grow faster with secure and scalable technology across India.
          </p>

          <div className="flex flex-row flex-wrap items-center justify-center lg:justify-start gap-2.5 sm:gap-4 w-full">
            <a
              href="#contact"
              className="btn-primary text-white !px-5 !py-2.5 sm:!px-8 sm:!py-3.5 group relative overflow-hidden w-auto inline-flex justify-center"
            >
              <span className="relative z-10 flex items-center gap-2">
                Start a Project
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 smooth-transition" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full smooth-transition" />
            </a>

            <a
              href="#products"
              className="btn-secondary hover-lift group relative w-auto inline-flex justify-center !px-5 !py-2.5 sm:!px-8 sm:!py-3.5"
            >
              <span className="flex items-center gap-2">
                <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                View Our Work
              </span>
            </a>
          </div>
        </div>

        {/* Enhanced Hero Graphic */}
        <div className="hidden lg:block relative">
          <div className="relative z-10">
            {/* Main Code Card */}
            <div className="card-elevated p-8 transform rotate-3 hover:rotate-0 smooth-transition duration-500 bg-gradient-to-br from-white/95 to-gray-50/95 dark:from-dark-800/95 dark:to-dark-700/95">
              {/* Card Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-lg" />
                  <div className="w-3 h-3 rounded-full bg-green-500 shadow-lg" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
                  <span className="text-caption font-mono">LIVE</span>
                </div>
              </div>

              {/* Enhanced Code Display */}
              <HeroCodeTyping />

              {/* Code Footer */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-caption">Ready to deploy</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-caption font-mono">v2.0.1</span>
                </div>
              </div>
            </div>

            {/* Enhanced Decorative Elements */}
            <div className="absolute -top-8 -right-8 w-full h-full border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-3xl -z-10 rotate-6 opacity-50" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-brand-400/20 to-accent-purple-400/20 rounded-full blur-xl animate-pulse-slow" />
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-accent-cyan-400/20 to-brand-500/20 rounded-full blur-lg animate-float animation-delay-1000" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
